/**
 * contact_picker_controller.js — recipient-contact picker for the quote form (Stimulus).
 *
 * Wraps the [data-contact-picker] block: a <select name="recipient_contact_id">
 * whose options reload per selected client, plus an inline "new contact" editor.
 *
 * Why this exists: for a business client (a real-estate agency, a strata
 * manager) the quote is addressed to a PERSON at that business, not to the
 * business itself. Without a picker here the only way to record the agent was
 * to create a whole new client per agent — which is exactly how one agency
 * ended up as four duplicate client records. One client, many contacts.
 *
 * Hidden entirely when the selected client is an individual: they are their own
 * contact, so the field would be noise.
 *
 * Modelled on site_picker_controller.js (fetch → render → create → select),
 * including its Turbo lifecycle: a connect-scoped turbo:before-cache destroys
 * Choices and clears the DOM-dataset init guards BEFORE Turbo snapshots the
 * page, so a cached <select> never returns with a dead Choices wrapper.
 *
 * Markup contract:
 *   <div data-controller="contact-picker" data-contact-picker
 *        data-client-select="#quote-client-select">
 *     <select name="recipient_contact_id" data-contact-picker-select></select>
 *     <div data-contact-picker-new-contact>
 *       <input data-contact-picker-new-contact-first_name> ...
 *       <button data-contact-picker-new-contact-save></button>
 *     </div>
 *   </div>
 */
import { Controller } from "@hotwired/stimulus";

    var NEW_CONTACT_VALUE = '__new__';

    var CONTACT_FIELDS = ['first_name', 'last_name', 'email', 'phone', 'role'];

    function destroyChoices(select) {
        if (select && select.choicesInstance) {
            select.choicesInstance.destroy();
            select.choicesInstance = null;
        }
    }

    function initChoices(select, retries) {
        if (!select || select.choicesInstance) return;
        if (typeof retries === 'undefined') retries = 5;
        if (typeof Choices === 'undefined') {
            if (retries > 0) {
                setTimeout(function () { initChoices(select, retries - 1); }, 100);
            }
            return;
        }
        select.choicesInstance = new Choices(select, {
            searchEnabled: true,
            placeholder: false,
            itemSelectText: '',
            shouldSort: false,
            searchPlaceholderValue: 'Search contacts...'
        });
    }

    function setPickerValue(select, value) {
        var str = String(value == null ? '' : value);
        if (select.choicesInstance) {
            select.choicesInstance.setChoiceByValue(str);
        } else {
            select.value = str;
        }
    }

    function fieldEl(picker, key) {
        return picker.querySelector('[data-contact-picker-new-contact-' + key + ']');
    }

    function readField(picker, key) {
        var el = fieldEl(picker, key);
        return el ? el.value : '';
    }

    function writeField(picker, key, value) {
        var el = fieldEl(picker, key);
        if (el) el.value = value || '';
    }

    function labelForContact(contact) {
        var parts = [contact.display_name || ('Contact #' + contact.id)];
        if (contact.role) parts.push(contact.role);
        if (contact.is_primary) parts.push('Primary');
        return parts.join(' - ');
    }

    async function fetchContacts(clientId) {
        var data = await window.contactApi.list(clientId);
        return (data && data.contacts) ? data.contacts : [];
    }

    function setLoading(select, isLoading) {
        if (isLoading) {
            destroyChoices(select);
            select.innerHTML = '<option value="">Loading contacts...</option>';
        }
        select.disabled = isLoading;
    }

    function renderContacts(select, contacts, selectedValue, allowCreate) {
        var currentValue = typeof selectedValue !== 'undefined'
            ? String(selectedValue || '') : String(select.value || '');
        destroyChoices(select);
        select.innerHTML = '';

        // Always offer "no particular contact": the quote may be addressed to
        // the business at large, which is what a null recipient means.
        var blank = document.createElement('option');
        blank.value = '';
        blank.textContent = contacts.length ? 'No specific contact' : 'No saved contacts';
        select.appendChild(blank);

        var effectiveValue = '';
        contacts.forEach(function (contact) {
            var option = document.createElement('option');
            option.value = contact.id;
            option.textContent = labelForContact(contact);
            if (currentValue && String(contact.id) === currentValue) {
                option.selected = true;
                effectiveValue = String(contact.id);
            } else if (!currentValue && contact.is_primary && !effectiveValue) {
                option.selected = true;
                effectiveValue = String(contact.id);
            }
            select.appendChild(option);
        });

        if (allowCreate) {
            var createOption = document.createElement('option');
            createOption.value = NEW_CONTACT_VALUE;
            createOption.textContent = '+ Add new contact';
            select.appendChild(createOption);
        }

        select.disabled = false;
        initChoices(select);
        // Choices.js can desync from the pre-selected <option> when re-wrapping;
        // force the visible chip and underlying value to match what we chose.
        if (effectiveValue) setPickerValue(select, effectiveValue);
    }

    function clearNewContactErrors(picker) {
        var error = picker.querySelector('[data-contact-picker-new-contact-error]');
        if (error) {
            error.textContent = '';
            error.classList.add('d-none');
        }
    }

    function setNewContactError(picker, message) {
        var error = picker.querySelector('[data-contact-picker-new-contact-error]');
        if (!error) return;
        error.textContent = message;
        error.classList.remove('d-none');
    }

    function clearNewContactInputs(picker) {
        CONTACT_FIELDS.forEach(function (key) { writeField(picker, key, ''); });
        clearNewContactErrors(picker);
    }

    function hideNewContactForm(picker) {
        var form = picker.querySelector('[data-contact-picker-new-contact]');
        if (form) form.classList.add('d-none');
        picker.dataset.contactPickerCreatingContact = 'false';
        clearNewContactErrors(picker);
    }

    function showNewContactForm(picker) {
        var form = picker.querySelector('[data-contact-picker-new-contact]');
        if (!form) return;
        form.classList.remove('d-none');
        picker.dataset.contactPickerCreatingContact = 'true';
        clearNewContactErrors(picker);
        var first = fieldEl(picker, 'first_name');
        if (first) first.focus();
    }

    async function createContact(picker, clientId) {
        var payload = {};
        CONTACT_FIELDS.forEach(function (key) {
            var value = readField(picker, key).trim();
            if (value) payload[key] = value;
        });

        if (!payload.first_name && !payload.last_name) {
            setNewContactError(picker, 'Enter a name before saving.');
            return null;
        }

        return await window.contactApi.create(clientId, payload, { acceptStream: false });
    }

    /** True when the chosen client is a business, so a contact makes sense. */
    function clientIsBusiness(clientSelect) {
        var option = clientSelect.options[clientSelect.selectedIndex];
        return !!(option && option.dataset.clientType === 'business');
    }

    function initPicker(picker) {
        if (picker.dataset.contactPickerReady === 'true') return;

        var select = picker.querySelector('[data-contact-picker-select]');
        var clientSelector = picker.dataset.clientSelect;
        var clientSelect = clientSelector ? document.querySelector(clientSelector) : null;
        var newContactSave = picker.querySelector('[data-contact-picker-new-contact-save]');
        var newContactCancel = picker.querySelector('[data-contact-picker-new-contact-cancel]');

        if (!select || !clientSelect) return;
        picker.dataset.contactPickerReady = 'true';
        picker.dataset.contactPickerCreatingContact = 'false';

        function setPickerVisible(visible) {
            picker.classList.toggle('d-none', !visible);
            // A hidden picker must not submit a stale id from the previous client.
            if (!visible) {
                hideNewContactForm(picker);
                clearNewContactInputs(picker);
                destroyChoices(select);
                select.innerHTML = '<option value=""></option>';
                select.value = '';
            }
        }

        async function load(preferredId) {
            var clientId = clientSelect.value;
            if (!clientId || !clientIsBusiness(clientSelect)) {
                setPickerVisible(false);
                return;
            }
            setPickerVisible(true);

            var selectedValue = typeof preferredId !== 'undefined' ? preferredId : select.value;
            try {
                setLoading(select, true);
                renderContacts(select, await fetchContacts(clientId), selectedValue, true);
            } catch (error) {
                console.warn('contact_picker: failed to load contacts', error);
                destroyChoices(select);
                select.innerHTML = '<option value="">Unable to load contacts</option>';
                select.disabled = false;
                initChoices(select);
            }
        }

        function openNewContactEditor() {
            if (!clientSelect.value) {
                setNewContactError(picker, 'Select a client first.');
                return;
            }
            var current = select.value || '';
            picker.dataset.contactPickerPreviousValue = current === NEW_CONTACT_VALUE ? '' : current;
            showNewContactForm(picker);
            setPickerValue(select, '');
        }

        async function saveNewContact() {
            var clientId = clientSelect.value;
            if (!clientId) {
                setNewContactError(picker, 'Select a client first.');
                return false;
            }

            var previousValue = select.value;
            try {
                if (newContactSave) newContactSave.disabled = true;
                if (newContactCancel) newContactCancel.disabled = true;
                var created = await createContact(picker, clientId);
                if (!created) return false;

                hideNewContactForm(picker);
                clearNewContactInputs(picker);
                picker.dataset.contactPickerPreviousValue = '';
                await load(String(created.id));
                select.focus();
                return true;
            } catch (error) {
                setPickerValue(select, picker.dataset.contactPickerPreviousValue || previousValue || '');
                setNewContactError(picker, error.message || 'Unable to create contact');
                return false;
            } finally {
                if (newContactSave) newContactSave.disabled = false;
                if (newContactCancel) newContactCancel.disabled = false;
            }
        }

        // Saved on the element, not a closure: _teardown has to remove this
        // listener by hand because the client <select> lives outside the
        // controller's element, so Stimulus never unbinds it.
        picker._onClientChange = function () {
            hideNewContactForm(picker);
            picker.dataset.contactPickerPreviousValue = '';
            select.value = '';
            load();
        };
        picker._clientSelect = clientSelect;
        clientSelect.addEventListener('change', picker._onClientChange);

        if (newContactSave) newContactSave.addEventListener('click', saveNewContact);
        if (newContactCancel) newContactCancel.addEventListener('click', function () {
            var previousValue = picker.dataset.contactPickerPreviousValue || '';
            hideNewContactForm(picker);
            clearNewContactInputs(picker);
            picker.dataset.contactPickerPreviousValue = '';
            setPickerValue(select, previousValue);
        });

        select.addEventListener('change', function () {
            if (select.value === NEW_CONTACT_VALUE) {
                openNewContactEditor();
                return;
            }
            if (picker.dataset.contactPickerCreatingContact === 'true') {
                hideNewContactForm(picker);
            }
        });

        load();
    }

export default class extends Controller {
        connect() {
            this._onBeforeCache = () => this._teardown();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
            initPicker(this.element);
        }

        disconnect() {
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this._teardown();
        }

        // Destroy Choices and clear the DOM-dataset init guard before Turbo
        // caches the snapshot. The guard would otherwise survive in the cached
        // markup and block re-init on restore while the Choices instance is gone.
        // The client <select> lives outside this element, so its listener has to
        // be removed by hand — Stimulus only unbinds what it bound.
        _teardown() {
            var picker = this.element;
            destroyChoices(picker.querySelector('[data-contact-picker-select]'));
            hideNewContactForm(picker);
            if (picker._clientSelect && picker._onClientChange) {
                picker._clientSelect.removeEventListener('change', picker._onClientChange);
                picker._clientSelect = null;
                picker._onClientChange = null;
            }
            delete picker.dataset.contactPickerReady;
        }
}
